import i18n from '../../../i18n';
import { cleanString, arraySortAscendingFunction } from '../../../services/utilities/utilities';
import { getFeatureInfoFromFilters, displayThemeAttribute } from '../../../services/map/feature/selection';
import {
    VALUE_INPUT_TYPE, NEDGLOBE_VIEWER, PRIMARY_DRAW_STYLE,
    PRIMARY_HIGHLIGHTED_DRAW_STYLE, TAB_STATUS, MAX_RESULTS_RELATION,
    NEDGLOBE_SERVICES, RELATIONSHIP_TYPES, TAB_TYPES
} from '../../../constants';
import { getCombinedOlId } from '../../../services/theme';

export const actions = {
    async searchParcels({ dispatch }, searchText) {
        // Formats identification as gemeentecode-sectie-perceelnummer as follows:
        // Gemeentecode: First 5 characters
        // Sectie: 6th character is section letter, 7th character is also section letter if not number
        // Perceelnummer: from 7th character if that is a digit otherwise from 8th character
        const regex = new RegExp(/^(.{5})[^\w\d]{0,}([A-za-z]{1,2})[^\w\d]{0,}(\d{1,})/, 'g');
        let result = [...searchText.matchAll(regex)];
        searchText = result.length > 0 ? `${result[0][1]}-${result[0][2]}-${result[0][3]}` : searchText;

        const searchResults = await dispatch('getData', { url: 'brk/getParcelSuggests', params: { parcelIdentification: searchText } });
        return searchResults?.data;
    },

    async getSearchSuggestionsForThemes({ dispatch, getters }, { searchText, searchableThemes }) {
        const activeLayers = getters.getActivatedLayers;
        const activeThemeAndAttributes = activeLayers.map(({ extended }) => ({
            themeId: extended?.themeId,
            informationAttributes: extended?.informationAttributes?.map(({ attribute }) => attribute)
        }));
        searchText = Buffer.from(cleanString(searchText.substring(0, 20)), 'utf8').toString('base64');
        const searchResults = await dispatch('useApiData', {
            name: 'Theme',
            parameters: {
                id: searchText,
                themelayerids: searchableThemes.join(',')
            }
        });

        return searchResults?.data
            .reduce((resultList, result) => {
                if (!result.userEnabled) {
                    return resultList;
                }
                if (result?.searchAttributes?.some(({ type }) => type === 'Attribute')) {
                    const themeId = result.id;
                    const hasFoundAtributeInActiveThemes = activeThemeAndAttributes
                        .filter(item => item.themeId === themeId)
                        .flatMap(({ informationAttributes }) => informationAttributes)
                        .some(attribute => result.searchAttributes.some(({ key }) => key === attribute));
                    if (!hasFoundAtributeInActiveThemes) {
                        return resultList;
                    }
                }
                resultList.push(result);
                return resultList;
            }, []);
    },

    async getWebthemeAtributeResult({ dispatch }, { searchText, webthemeId }) {
        const webthemeAttribute = {
            term: Buffer.from(cleanString(searchText), 'utf8').toString('base64'),
            webthemeId: webthemeId
        };

        const searchResults = await dispatch('useApiData', {
            name: 'WebGetWebThemeAttributes',
            data: webthemeAttribute
        });
        const themeId = searchResults.data?.themeId;
        return searchResults.data?.attributes?.map(result => ({ ...result, webthemeId, themeId }));
    },

    async getGeoCode({ state, getters, dispatch }, searchText) {
        searchText = Buffer.from(cleanString(searchText), 'utf8').toString('base64');
        const organizationId = getters.isMobile ? state.userData.organizationId : getters.getOrganizationId;
        const webThemeId = getters.isMobile ? null : getters.getWebTheme.id;
        let name = 'GeoCode';
        if (getters.isWeb) {
            name = 'WebGeoCode';
        }
        const searchResults = await dispatch('useApiData', {
            name: name,
            parameters: { id: searchText, organizationId, webThemeId }
        });
        return searchResults?.data;
    },

    async getLookupData({ state, getters, dispatch }, addressId) {
        let name = 'Lookup';
        if (getters.isWeb) {
            name = 'WebLookup';
        }
        const searchResults = await dispatch('useApiData', {
            name: name,
            parameters: { id: addressId }
        });
        return searchResults.data;
    },

    async searchFeaturesOnLayerWithFilter(store, { bbox, layerId, key, value, operator = '=' }) {
        const userData = store.getters.getUserData;
        const layer = store.getters.getLayerById(layerId);
        if (!layer) {
            throw new Error('layer not found');
        }
        const informationAttribute = layer.extended.informationAttributes.find(({ attribute }) => attribute === key);
        // skip if attribute is not found
        if (!informationAttribute) {
            throw new Error('informationAttribute not found');
        }
        let filterSettings = layer.filters?.data?.find(({ settings }) => settings.informationAttributeId === informationAttribute.id);
        if (!filterSettings) {
            filterSettings = {
                settings: {
                    dataType: 'xsd:string',
                    // filterwaarde may not be editted
                    domainId: null,
                    errorMessage: null,
                    feedbackDomain: null,
                    // attribute name for the CQL query
                    filterAttribute: informationAttribute.attribute,
                    // only attribute filters have got an id (taken from the database)
                    // Even negative id's should be unique
                    // id: -1 * informationAttribute.id,
                    // attribute for this searchfilter
                    informationAttributeId: informationAttribute.id,
                    // attribute name for the user interface
                    name: informationAttribute.alias,
                    // operator for the CQL query
                    operator,
                    // take a high value for order, the search filter will be shown as the top layer
                    // order: 9000,
                    // filter is checked by default
                    selected: true,
                    // themeId: theme.id,
                    themeLayerId: layerId,
                    // unavailable: false,
                    // Copy filtervalue from the searchterm
                    value: value,
                    // in case of 'Alle waarden tonen' the values property hold all domain values selected on the filter tab
                    // this is not used for search filters
                    domainValues: [],
                    valueInput: VALUE_INPUT_TYPE.TEXTFIELD,
                    // visible: true
                }
            };
        }
        const filter = { ...filterSettings.settings, value };

        return getFeatureInfoFromFilters(
            store,
            layer,
            [filter],
            bbox,
            userData
        );
    },

    searchFeaturesOnLayerWithCustomFilters(store, { bbox, layerId, filters, multipleFilters = false }) {
        const userData = store.getters.getUserData;
        const layer = store.getters.getLayerById(layerId);
        if (!layer) {
            throw new Error('layer not found');
        }
        return getFeatureInfoFromFilters(
            store,
            layer,
            filters,
            bbox,
            userData,
            multipleFilters
        );
    },

    async getUserSearches({ state, dispatch, commit }, { force }) {
        if (!force && state.userSearches) {
            return state.userSearches;
        }
        const userSearchesResult = await dispatch('useApiData', { name: 'GetUserSearches' });
        const userSearches = userSearchesResult.data?.map(result => ({ ...result, useViewPort: result?.content?.viewport !== undefined }));
        commit('SET_USERSEARCHES', userSearches);
        return userSearches;
    },

    async addUserSearch({ dispatch }, data) {
        await dispatch('useApiData', { name: 'CreateUserSearch', useLoader: true, data });
        return dispatch('getUserSearches', { force: true });
    },

    async deleteUserSearch({ dispatch }, id) {
        await dispatch('useApiData', { name: 'DeleteUserSearch', useLoader: true, parameters: id });
        return dispatch('getUserSearches', { force: true });
    },

    async selectAndZoomToApiResultPointFeatures(store, { data }) {
        if (data?.features && data.features.length > 0) {
            if (!data.pointInsideBbox) {
                store.commit('ADD_STATUS_MESSAGE', {
                    message: i18n.t('addressOutsideOrganizationPolygon'),
                    variant: 'danger',
                    autoHideDelay: 5000
                });
            }
            const focusLayerName =
                NEDGLOBE_VIEWER.customLayers.printable.selections;
            const mapConfig = store.state.maps.find(
                (_map) => _map.name === 'main'
            );
            const focusLayer = mapConfig.customLayers[focusLayerName];
            focusLayer.setStyle(data?.highlight ? PRIMARY_HIGHLIGHTED_DRAW_STYLE : PRIMARY_DRAW_STYLE);
            store.commit('SHOW_MAP_FEATURE', {
                mapName: 'main',
                customLayerName: focusLayerName,
                features: data.features,
                reset: true
            });

            if (data.zoomToFeatures) {
                data.zoomToFeatures = false;
                store.dispatch('zoomToFeatures', {
                    mapName: 'main',
                    features: data.features,
                    options: { duration: 500 }
                });
            }
        }
    },

    initTabObjectSearch(store, { title }) {
        // Close detail panel
        store.commit('TOGGLE_OBJECT_DETAIL_PANEL', false);
        // Clear existing data with this title
        store.commit('CLEAR_OBJECT_DATA', { title });
        store.commit('CLEAR_TABORDER_DATA', { title });

        store.commit('SET_TAB_OBJECT_SEARCH', { title });

        // Open tab with loading message
        store.commit('SET_OBJECT_DATA_LOADING_TAB', { title });
    },

    // remove all tabs and the data after the tab with the given title.
    clearTabsRight({ dispatch, state, commit }, { title }) {

        if (title && state.tabOrder.length !== 0) {
            const index = state.tabOrder.findIndex(tO => tO.title === title);
            if (index !== -1) {
                //  Loop through all tabs after the found tab and delete data
                for (let i = index + 1; i < state.tabOrder.length; i++) {
                    // depending on the type of data in the tab, remove that data.
                    if (state.tabOrder[i].type === TAB_TYPES.POINTER) {
                        dispatch('removeNedGlobePointer');
                        dispatch('clearParcelContour');
                    } else if (state.tabOrder[i].type === TAB_TYPES.LAYER) {
                        dispatch('removeTabGroup', { title: state.tabOrder[i].title });
                    } else if (state.tabOrder[i].type === TAB_TYPES.ADMIN) {
                        dispatch('removeTabObjectSearch', {
                            title: state.tabOrder[i].title
                        });
                    }
                }
                // finally clear the taborder array after the title
                commit('CLEAR_TABORDER_RIGHT', { title });
            }
        }
    },
    addLayerTab({ commit }, { title }) {
        commit('ADD_LAYER_TAB', { title });
    },
    setTabToFocus({ commit }, { title }) {
        commit('SET_TAB_TO_FOCUS', { title });
    },
    clearTabToFocus({ commit }) {
        commit('CLEAR_TAB_TO_FOCUS');
    },

    removeTabObjectSearch(store, { title }) {
        if (title) {
            store.commit('CLEAR_OBJECT_DATA', { title });
            store.commit('CLEAR_SELECTION_OBJECT_DATA', { title });
            store.commit('CLEAR_TABORDER_DATA', { title });
        } else {
            store.commit('SET_TAB_OBJECT_SEARCH', {});
            store.commit('CLEAR_OBJECT_DATA', {});
        }
    },

    removeTabFromTabOrder(store, { title }) {
        store.commit('CLEAR_TABORDER_DATA', { title });
    },
    updateObjectSearchPointer(store, { index }) {
        const data = store.state.objectSearchResults[index];
        if (data?.cadastralProperties) {
            store.dispatch('selectAndZoomToFeaturesKadastraal', { data });
        }
        if (data?.naturalPersons || data?.kvkTradeRegister || data?.brpPersons || data?.wozData) {
            store.dispatch('selectAndZoomToApiResultPointFeatures', { data });
        }
    },

    async getAuthorizedTableSearchResults({ state, getters, commit, dispatch }) {
        const tableSearchResults = await dispatch('useApiData', { name: 'GetAuthorizedTableSearchResults' });
        const allRelations = await dispatch('useApiData', { name: 'GetAllAuthorizedTableTableRelations' });

        let allResult;
        if (allRelations?.data) {
            allResult = allRelations.data.map((relation) => {
                if (typeof relation.settings === 'string') {
                    return {
                        ...relation,
                        type: RELATIONSHIP_TYPES.Table2Table,
                        settings: JSON.parse(relation.settings)
                    };
                }
                return relation;
            });
        }

        // add relations to tableSearchresults
        if (allResult) {
            for (const tableSearchResult of tableSearchResults.data) {
                tableSearchResult.relations = allResult.filter(r => r.fromTableSearchResultId === tableSearchResult.id);
            }
        }

        commit('SET_TABLE_SEARCHRESULTS', tableSearchResults.data?.sort(arraySortAscendingFunction('title')));

        if (getters.isMobile) {

            // When a table has a relation to a themelayer make sure the themelayer is loaded into state
            // so that the relation can be found in state.layers
            let themeLayerRelations = [];

            let tableIds = tableSearchResults.data.map(({ id }) => id);

            const allRelations = await dispatch('useApiData', {
                name: 'GetThemeLayerTableRelationsByTableIds',
                data: {
                    ids: tableIds
                }
            });

            if (allRelations.data) {
                themeLayerRelations = allRelations.data;
            }

            // For every theme that is available in this session
            // check if the theme contains the themelayer and if so
            // load that theme-themelayer into state if not already there  (if the layer is active on opening the
            // session the layer is already in state at this point)
            for (let themeLayerRelation of themeLayerRelations) {

                // zoek layer onder theme
                const themes = state.themes.filter(t => t.data?.themeLayers.some(({ id }) => id === themeLayerRelation.fromThemeLayerId));

                // for all themes that have this layer
                for (const theme of themes) {
                    const customId = getCombinedOlId(theme.id, themeLayerRelation.fromThemeLayerId);
                    const layerData = getters.getLayerById(customId);

                    // Layer not already in state. Add as active and then immediately set not active
                    if (!layerData) {
                        await dispatch('toggleThemeLayer', {
                            mapName: 'main',
                            themeId: theme.id,
                            id: themeLayerRelation.fromThemeLayerId,
                            active: true
                        });
                        await dispatch('toggleThemeLayer', {
                            mapName: 'main',
                            themeId: theme.id,
                            id: themeLayerRelation.fromThemeLayerId,
                            active: false
                        });
                    }

                }
            }
        }

    },

    async getTableSearchFields({ getters, dispatch }, id) {
        const tableSearchResult = getters.getTableSearchResultById(id);
        if (!tableSearchResult.filters) {
            const searchFieldResults = await dispatch('useApiData', { name: 'GetTableSearchFields', parameters: { Id: id } });
            tableSearchResult.filters = searchFieldResults?.data
                ?.sort((a, b) => a.order - b.order)
                ?.map((item) => {
                    item.visible = true;
                    item.selected = false;
                    item.name = item.alias;
                    if (item.feedbackDomain) {
                        item.feedbackDomain.keyvalues = item.feedbackDomain?.feedbackDomainKvs
                            .map((keyvalue, index) => {
                                return {
                                    id: index,
                                    ...keyvalue,
                                };
                            });
                    }
                    return { settings: item };
                });
        }
    },

    async searchTableData({ commit, getters, dispatch }, { title, id, objectData }) {
        let returnVal = false;

        // Clear possible results for this title and open new tab with status Loading
        dispatch('initTabObjectSearch', { title });

        try {
            const tableResult = await dispatch('useApiData', { name: 'GetTableSearchResult', parameters: { Id: id } });
            const tableSettings = Object.assign({}, tableResult?.data);

            const foundResults = await dispatch('useApiData', {
                name: 'SearchTableData',
                parameters: { Id: id },
                data: objectData.map(({ columnName, dataType, operator, value, caseSensitive }) => ({
                    columnName,
                    dataType,
                    operator,
                    value: Array.isArray(value) ? value : [value],
                    caseSensitive
                }))
            });

            let items = [];
            let fields = [];
            let maxResultsExceeded = false;

            if (foundResults.data && foundResults.data.length > 0) {
                items = JSON.parse(foundResults.data);
                maxResultsExceeded = items.length > MAX_RESULTS_RELATION;
                if (maxResultsExceeded) {
                    items.splice(-1);
                }
                items.forEach((item, index) => {
                    // provide a unique ID column to avoid conflicts with the ID column in the data
                    item.nedGlobeRowId = index;
                });
                fields = tableSettings.tableSearchResultFields
                    .filter(attribute => {
                        return displayThemeAttribute(attribute);
                    })
                    .map(attribute => (
                        {
                            ...attribute,
                            key: attribute.columnName,
                            label: attribute.alias,
                            sortable: true,
                        }
                    ))
                    .sort((a, b) => a.order - b.order);
            }
            returnVal = (items.length > 0);

            commit('UPDATE_LOADING_OBJECT_DATA', {
                title,
                data: {
                    items,
                    fields,
                    selectable: true,
                    tableSearchResult: id,
                    isAdvancedSearch: true
                },
                message: maxResultsExceeded ? i18n.t('relations.maxResultsExceeded', [MAX_RESULTS_RELATION]) : '',
                count: items.length,
                exportable: true,
                download: true,
                printable: true,
                status: TAB_STATUS.READY
            });

            // if tableresult has a relation with a layer, get related features of that layer
            // use these features for selecting/highlighting
            // if a tableresult has multiple relations, always use the first one with a relation to a layer
            const tableResultRelations = getters.getTableResultRelations(id)
                .filter(({ type }) => type === RELATIONSHIP_TYPES.ThemeLayer2Table);
            if (tableResultRelations?.length > 0) {
                const firstRelation = tableResultRelations[0];
                const themeLayer = getters.getLayerByOriginalId(
                    firstRelation.fromThemeLayerId
                );
                if (themeLayer?.data.source.name === NEDGLOBE_SERVICES) {
                    await dispatch('findReversedTableRelation', {
                        title,
                        data: items,
                        relation: { ...firstRelation },
                        isAdvancedSearch: true
                    });
                }
            }
        } catch (err) {
            commit('UPDATE_LOADING_OBJECT_DATA', {
                title,
                data: {
                    items: [],
                    fields: []
                },
                count: 0,
                status: TAB_STATUS.ERROR,
                message: err.message
            });
            returnVal = false;
        }
        return returnVal;
    }
};
